/**
 * Quadro ao lado do equipamento no SCADA 3D: valores ao vivo e gráfico de linha da última hora — Nexora Grid.
 *
 * Criado em: 17/10/2026 · Última modificação: 18/10/2026 · Versão: 1.1.0
 *
 * Retorno do responsável do produto (17/10/2026): "é muito importante termos os quadrados com as informações ao lado do
 * equipamento" e "um quadro com um gráfico da última hora, gráfico de linha". O histórico vem de
 * `/api/v1/tempo-real/historico` (atualizado a cada minuto) e os valores atuais do canal de tempo real.
 * v1.1 (retorno de 18/10/2026: "não pode estar em cima do desenho do cavalete"): o quadro é posicionado a cada quadro
 * ao lado do CONTORNO do modelo na tela (caixa do modelo projetada): à direita; sem espaço, à esquerda; alinhado ao
 * topo do desenho e preso dentro da tela. Uma linha liga o quadro ao equipamento. Também desvia dos painéis fixos
 * do palco (setores, detalhe, setor, quadro de comando): tenta direita, esquerda, acima e abaixo do desenho. Sem lugar
 * livre, o quadro secundário se esconde; o principal (equipamento em foco) fica onde menos cobre.
 */
import { Html } from "@react-three/drei";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { api } from "../api";
import type { Equipamento } from "./rede";

export type Leitor = (deviceId: string | null | undefined, medida: string) => number | null;
type Linha = { rotulo: string; medida: string; unidade: string; casas?: number; formatar?: (v: number) => string };

const LINHAS: Record<string, { grafico: string; titulo: string; linhas: Linha[] }> = {
  ponto_pressao: { grafico: "pressao_rede_mca", titulo: "Pressão (1 h)", linhas: [
    { rotulo: "Pressão na rede", medida: "pressao_rede_mca", unidade: "mca", casas: 1 }] },
  reservatorio: { grafico: "nivel_reservatorio_pct", titulo: "Nível (1 h)", linhas: [
    { rotulo: "Nível", medida: "nivel_reservatorio_pct", unidade: "%", casas: 0 },
    { rotulo: "Altura da água", medida: "nivel_m", unidade: "m", casas: 2 },
    { rotulo: "Entrada", medida: "vazao_entrada_m3h", unidade: "m³/h", casas: 1 },
    { rotulo: "Saída", medida: "vazao_saida_m3h", unidade: "m³/h", casas: 1 }] },
  macromedidor: { grafico: "vazao_instantanea_m3h", titulo: "Vazão (1 h)", linhas: [
    { rotulo: "Vazão", medida: "vazao_instantanea_m3h", unidade: "m³/h", casas: 1 },
    { rotulo: "Pressão na rede", medida: "pressao_rede_mca", unidade: "mca", casas: 1 },
    { rotulo: "Volume", medida: "volume_total_acumulado_m3", unidade: "m³", casas: 0 }] },
  vrp: { grafico: "pressao_jusante_mca", titulo: "Pressão a jusante (1 h)", linhas: [
    { rotulo: "Jusante", medida: "pressao_jusante_mca", unidade: "mca", casas: 1 },
    { rotulo: "Montante", medida: "pressao_montante_mca", unidade: "mca", casas: 1 },
    { rotulo: "Vazão", medida: "vazao_instantanea_m3h", unidade: "m³/h", casas: 1 }] },
  bomba: { grafico: "corrente_fase_a", titulo: "Corrente (1 h)", linhas: [
    { rotulo: "Estado", medida: "bomba_ligada", unidade: "", formatar: (v) => (v >= 0.5 ? "ligada" : "desligada") },
    { rotulo: "Corrente", medida: "corrente_fase_a", unidade: "A", casas: 1 },
    { rotulo: "Tensão", medida: "tensao_fase_v", unidade: "V", casas: 0 },
    { rotulo: "Potência", medida: "potencia_ativa_kw", unidade: "kW", casas: 1 },
    { rotulo: "Vazão", medida: "vazao_instantanea_m3h", unidade: "m³/h", casas: 1 },
    { rotulo: "Temperatura", medida: "temperatura_bomba_c", unidade: "°C", casas: 1 }] },
};
const definicao = (tipo: string) => LINHAS[tipo] ?? (["bomba", "poco_profundo", "elevatoria"].includes(tipo) ? LINHAS.bomba : null);

/** Série da última hora de uma medida (atualiza a cada minuto). */
function useHistorico(empresa: string | null, device: string | null | undefined, medida: string, minutos: number, alternativa?: string) {
  const chave = `${empresa}|${device}|${medida}|${minutos}|${alternativa}`;
  type Historico = { pontos: { t: number; v: number }[]; medida: string; erro: boolean; carregando: boolean; chave: string };
  const [serie, setSerie] = useState<Historico>({ pontos: [], medida, erro: false, carregando: true, chave: "" });
  useEffect(() => {
    if (!empresa || !device) return;
    let vivo = true;
    const carregar = () => api<{ series: Record<string, [string, number][]> }>(
      `/v1/tempo-real/historico?empresa=${encodeURIComponent(empresa)}&dispositivo=${encodeURIComponent(device)}&medidas=${[medida, alternativa].filter(Boolean).join(",")}&minutos=${minutos}`)
      .then((r) => {
        const escolhida = r.series[medida]?.length ? medida : alternativa && r.series[alternativa]?.length ? alternativa : medida;
        if (vivo) setSerie({ pontos: (r.series[escolhida] ?? []).map(([t, v]) => ({ t: Date.parse(t), v })),
          medida: escolhida, erro: false, carregando: false, chave });
      }).catch(() => { if (vivo) setSerie({ pontos: [], medida, erro: true, carregando: false, chave }); });
    void carregar();
    const relogio = window.setInterval(carregar, 60000);
    return () => { vivo = false; window.clearInterval(relogio); };
  }, [empresa, device, medida, alternativa, minutos, chave]);
  return serie.chave === chave ? serie : { pontos: [], medida, erro: false, carregando: true };
}

function GraficoLinha({ pontos }: { pontos: { t: number; v: number }[] }) {
  const L = 196, A = 52;
  if (pontos.length < 2) return <span className="suave">Sem histórico suficiente neste período</span>;
  const valores = pontos.map((p) => p.v);
  const min = Math.min(...valores), max = Math.max(...valores), faixa = max - min || 1;
  const t0 = pontos[0].t, duracao = pontos.at(-1)!.t - t0 || 1;
  const path = pontos.map((p, i) => `${!i || p.t - pontos[i - 1].t > 300000 ? "M" : "L"}${(p.t - t0) / duracao * L},${A - 4 - ((p.v - min) / faixa) * (A - 12)}`).join(" ");
  return (
    <svg width={L} height={A} className="grafico-hora" role="img" aria-label={`última hora: de ${min.toFixed(1)} a ${max.toFixed(1)}`}>
      <path d={path} fill="none" stroke="#22d3ee" strokeWidth="1.6" />
      <text x={2} y={9}>{max.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}</text>
      <text x={2} y={A - 1}>{min.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}</text>
    </svg>
  );
}

const fmt = (l: Linha, v: number | null) => (v === null ? "—" : l.formatar ? l.formatar(v)
  : `${v.toLocaleString("pt-BR", { maximumFractionDigits: l.casas ?? 1, minimumFractionDigits: l.casas ?? 0 })} ${l.unidade}`);

/** Bloco de um equipamento (usado sozinho ou para cada bomba de uma estação). */
function Bloco({ e, empresa, ler, comGrafico }: { e: Equipamento; empresa: string | null; ler: Leitor; comGrafico: boolean }) {
  const d = definicao(e.tipo);
  const [minutos, setMinutos] = useState(60);
  const reservatorio = e.tipo === "reservatorio";
  const serie = useHistorico(comGrafico ? empresa : null, e.deviceId, reservatorio ? "nivel_m" : d?.grafico ?? "", minutos,
    reservatorio ? "nivel_reservatorio_pct" : undefined);
  if (!d) return null;
  const sem = !e.deviceId;
  return (
    <div className="bloco-quadro">
      <strong>{e.id}</strong>
      {sem ? <span className="suave">sem telemetria ligada</span> : (
        <>
          {!d.linhas.some((l) => ler(e.deviceId, l.medida) !== null) && <span className="suave">Sem leitura recente</span>}
          <dl>{d.linhas.map((l) => { const v = ler(e.deviceId, l.medida); return v === null ? null : <div key={l.medida}><dt>{l.rotulo}</dt><dd>{fmt(l, v)}</dd></div>; })}</dl>
          {comGrafico && <>
            <span className="suave titulo-grafico">{reservatorio ? serie.medida === "nivel_m" ? "Altura da água" : "Nível (%)" : d.titulo.replace(" (1 h)", "")} ({minutos / 60} h)</span>
            {reservatorio && <div className="periodo-quadro" role="group" aria-label={`Período de ${e.id}`}>
              {[60, 360].map((m) => <button key={m} type="button" aria-pressed={minutos === m} onClick={() => setMinutos(m)}>{m / 60} h</button>)}
            </div>}
            {serie.carregando ? <span className="suave">Carregando histórico…</span> : serie.erro ? <span className="suave">Não foi possível carregar o histórico</span>
              : <GraficoLinha pontos={serie.pontos} />}
            {serie.pontos.length > 0 && <small className="suave">Último registro: {new Date(serie.pontos.at(-1)!.t).toLocaleString("pt-BR")} · {serie.pontos.at(-1)!.v.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} {serie.medida === "nivel_m" ? "m" : reservatorio ? "%" : ""}</small>}
          </>}
        </>
      )}
    </div>
  );
}

/** Blocos de um ou mais equipamentos (usado no quadro ancorado e no painel do quadro de comando, dentro da casa). */
export function BlocosQuadro({ membros, empresa, ler }: { membros: Equipamento[]; empresa: string | null; ler: Leitor }) {
  return <>{membros.map((m, k) => <Bloco key={m.id + k} e={m} empresa={empresa} ler={ler} comGrafico={k < 3} />)}</>;
}

const MARGEM = 12;
const CANTOS = [0, 1].flatMap((x) => [0, 1].flatMap((y) => [0, 1].map((z) => [x, y, z])));

/** Retângulo do objeto na tela (caixa 3D projetada); null se não há caixa. */
function contornoNaTela(objeto: THREE.Object3D, camera: THREE.Camera, largura: number, altura: number) {
  // só o que está visível (peças ocultas da estação e o corte do poço de longe não contam)
  const caixa = new THREE.Box3();
  const parte = new THREE.Box3();
  objeto.updateMatrixWorld(true);
  objeto.traverseVisible((o) => {
    const sprite = o as THREE.Sprite;
    if (sprite.isSprite) {   // rótulos (ex.: níveis do poço): o quadrado do texto, a partir do centro do sprite
      const c = sprite.center;
      parte.set(new THREE.Vector3(-c.x, -c.y, 0), new THREE.Vector3(1 - c.x, 1 - c.y, 0)).applyMatrix4(sprite.matrixWorld);
      caixa.union(parte);
      return;
    }
    const malha = o as THREE.Mesh;
    if (!malha.isMesh || !malha.geometry) return;
    if (!malha.geometry.boundingBox) malha.geometry.computeBoundingBox();
    caixa.union(parte.copy(malha.geometry.boundingBox!).applyMatrix4(malha.matrixWorld));
  });
  if (caixa.isEmpty()) return null;
  const p = new THREE.Vector3();
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
  for (const [cx, cy, cz] of CANTOS) {
    p.set(cx ? caixa.max.x : caixa.min.x, cy ? caixa.max.y : caixa.min.y, cz ? caixa.max.z : caixa.min.z).project(camera);
    const sx = (p.x + 1) / 2 * largura, sy = (1 - p.y) / 2 * altura;
    x0 = Math.min(x0, sx); x1 = Math.max(x1, sx); y0 = Math.min(y0, sy); y1 = Math.max(y1, sy);
  }
  return { x0, x1, y0, y1 };
}

type Retangulo = { x0: number; x1: number; y0: number; y1: number };

/** Painéis fixos do palco (setores, detalhe, setor, quadro de comando), em coordenadas do palco. */
function obstaculos(): Retangulo[] {
  const palco = document.querySelector(".palco-3d");
  if (!palco) return [];
  const base = palco.getBoundingClientRect();
  return [...palco.querySelectorAll(":scope > .painel")].map((el) => {
    const r = el.getBoundingClientRect();
    return { x0: r.left - base.left, x1: r.right - base.left, y0: r.top - base.top, y1: r.bottom - base.top };
  });
}

const sobrepoe = (a: Retangulo, b: Retangulo) => a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
const area = (a: Retangulo, b: Retangulo) => Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0)) * Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));

/** Quadro HTML ao lado do desenho do equipamento (nunca em cima dele). */
export function Quadro3d({ posicao, altura, principal, membros, empresa, ler, rotulo, alvo, prioritario = true }: {
  posicao: THREE.Vector3; altura: number; principal: Equipamento; membros: Equipamento[]; empresa: string | null; ler: Leitor; rotulo?: string;
  /** modelo detalhado do equipamento (o quadro fica fora do contorno dele) */
  alvo?: () => THREE.Object3D | null;
  /** quadro principal (foco): nunca se esconde */
  prioritario?: boolean;
}) {
  const lista = membros.length ? membros : [principal];
  const caixa = useRef<HTMLDivElement>(null);
  const linha = useRef<SVGLineElement>(null);
  const calcular = useMemo(() => (el: THREE.Object3D, camera: THREE.Camera, size: { width: number; height: number }) => {
    const w = caixa.current?.offsetWidth ?? 260, h = caixa.current?.offsetHeight ?? 200;
    const ancora = new THREE.Vector3().setFromMatrixPosition(el.matrixWorld).project(camera);
    const ax = (ancora.x + 1) / 2 * size.width, ay = (1 - ancora.y) / 2 * size.height;
    const modelo = alvo?.();
    const r = modelo ? contornoNaTela(modelo, camera, size.width, size.height) : null;
    const ret = r ?? { x0: ax - 20, x1: ax + 20, y0: ay - 20, y1: ay + 20 };
    // candidatos: direita, esquerda, acima e abaixo do desenho; vale o primeiro dentro da tela, fora do desenho e dos
    // painéis; se nenhum, o que menos cobre desenho + painéis
    const prender = (v: number, max: number) => Math.min(Math.max(v, 8), Math.max(8, max - 8));
    const yLado = prender(ret.y0, size.height - h);
    const xMeio = prender((ret.x0 + ret.x1) / 2 - w / 2, size.width - w);
    const candidatos = [[ret.x1 + MARGEM, yLado], [ret.x0 - MARGEM - w, yLado], [xMeio, ret.y0 - MARGEM - h], [xMeio, ret.y1 + MARGEM]];
    const bloqueios = [ret, ...obstaculos()];
    let melhor = candidatos[0], menor = Infinity;
    for (const [cx, cy] of candidatos) {
      const q = { x0: cx, x1: cx + w, y0: cy, y1: cy + h };
      const fora = cx < 4 || cy < 4 || q.x1 > size.width - 4 || q.y1 > size.height - 4;
      if (!fora && !bloqueios.some((b) => sobrepoe(q, b))) { melhor = [cx, cy]; menor = -1; break; }
      const custo = bloqueios.reduce((t, b) => t + area(q, b), 0) + (fora ? 1e7 : 0);
      if (custo < menor) { menor = custo; melhor = [cx, cy]; }
    }
    if (menor >= 0 && !prioritario) return [-10000, -10000];   // sem lugar livre: o secundário sai da tela
    const x = prender(melhor[0], size.width - w), y = prender(melhor[1], size.height - h);
    if (linha.current) {   // linha do quadro até o equipamento (coordenadas relativas ao quadro)
      const lado = x > ax ? 0 : w;
      linha.current.setAttribute("x1", String(lado));
      linha.current.setAttribute("y1", "14");
      linha.current.setAttribute("x2", String(ax - x));
      linha.current.setAttribute("y2", String(ay - y));
    }
    return [x, y];
  }, [alvo, prioritario]);
  return (
    <Html position={[posicao.x, posicao.y + altura, posicao.z]} zIndexRange={[60, 10]} className="quadro-3d-ancora" calculatePosition={calcular}>
      <svg className="quadro-3d-ligacao" aria-hidden="true"><line ref={linha} /></svg>
      <div ref={caixa} className="quadro-3d" role="group" aria-label={`Quadro de ${principal.id}`}>
        {rotulo && <span className="titulo-quadro">{rotulo}</span>}
        {lista.map((m, k) => <Bloco key={m.id + k} e={m} empresa={empresa} ler={ler} comGrafico={k < 2} />)}
      </div>
    </Html>
  );
}
